/**
 * VT323 (SIL OFL, bundled in public/fonts/) — the canonical DEC-VT220 terminal
 * font for the mainframe aesthetic. This module preloads it into
 * `document.fonts` so the §9 HUD type scale paints in VT323 from the first frame
 * (no Courier flash).
 *
 * BL-156 / D-254 (S157): the canvas title-bake + animated-title machinery
 * (`bakeTitle` / `startAnimatedTitle` / `renderPixelTitle` + palettes) was
 * retired dead code that still shipped — trimmed. The live boot title renders
 * elsewhere; only the font preload remains here.
 */

const FONT_FAMILY = "VT323";
const FONT_URL = "/fonts/VT323-Regular.ttf";

/** Lazy-loaded singleton so we register the font exactly once. */
let fontReady: Promise<void> | null = null;

/**
 * Register the VT323 `FontFace` into `document.fonts` once. Exported as
 * {@link ensureUIFont} to make VT323 DOM-ready early so the §9 HUD type scale
 * renders without a Courier flash. Resolves immediately in headless / SSR.
 */
function ensureFont(): Promise<void> {
  if (fontReady) return fontReady;
  if (typeof document === "undefined" || !("fonts" in document)) {
    // jsdom / SSR — skip; the UI falls back to system monospace.
    fontReady = Promise.resolve();
    return fontReady;
  }
  fontReady = (async () => {
    try {
      const face = new FontFace(FONT_FAMILY, `url(${FONT_URL})`);
      await face.load();
      document.fonts.add(face);
    } catch {
      // Font failed to load — the UI still works with the monospace fallback.
    }
  })();
  return fontReady;
}

/** Make the VT323 `FontFace` DOM-ready (added to `document.fonts`) so the HUD's
 *  §9 type scale paints in VT323 from the first frame — no Courier flash. Call
 *  early in bootstrap. Resolves immediately in headless / when already loaded. */
export const ensureUIFont = ensureFont;
